import { Controller } from "@hotwired/stimulus";
import JustValidate from "just-validate";

export default class extends Controller {
    static targets = ["form", "passwordPanel"];

    connect() {
        this.validator = new JustValidate(this.formTarget, {
            validateBeforeSubmitting: true,
            lockForm: true,
            focusInvalidField: true,
            errorFieldCssClass: "is-invalid",
        });

        // Fields
        this.validator
            .addField('[name="parent[first_name]"]', [
                { rule: "required", errorMessage: "First name is required" },
            ])
            .addField('[name="parent[last_name]"]', [
                { rule: "required", errorMessage: "Last name is required" },
            ]);

        // Password inputs
        this.currentInput = this.formTarget.querySelector('input[name="password[current_password]"]');
        this.newInput = this.formTarget.querySelector('input[name="password[new_password]"]');
        this.confirmInput = this.formTarget.querySelector('input[name="password[new_password_confirmation]"]');

        if (this.isPasswordPanelVisible()) {
            this.enablePasswordValidation();
        } else {
            this.disablePasswordValidation();
        }

        // ✅ Key: submit natively after validation success
        this.validator.onSuccess(() => {
            // Use native submit to avoid Turbo’s interception issues
            const loaderController = this.application.getControllerForElementAndIdentifier(
                this.formTarget,
                "form-loader"
            );
            if (loaderController) loaderController.showLoader();
            this.formTarget.submit();
        });
    }

    togglePasswordPanel() {
        if (!this.hasPasswordPanelTarget) return;
        this.passwordPanelTarget.classList.toggle("d-none");
        if (this.isPasswordPanelVisible()) {
            this.enablePasswordValidation();
        } else {
            this.disablePasswordValidation();
        }
    }

    isPasswordPanelVisible() {
        return this.hasPasswordPanelTarget && !this.passwordPanelTarget.classList.contains("d-none");
    }

    enablePasswordValidation() {
        [this.currentInput, this.newInput, this.confirmInput].forEach((el) =>
            el?.setAttribute("required", "required")
        );

        if (this.currentInput) {
            this._safeRemoveField(this.currentInput);
            this.validator.addField(this._sel(this.currentInput), [
                { rule: "required", errorMessage: "Current password is required" },
            ]);
        }

        if (this.newInput) {
            this._safeRemoveField(this.newInput);
            this.validator.addField(this._sel(this.newInput), [
                { rule: "required", errorMessage: "New password is required" },
                {
                    validator: () => {
                        if (!this.confirmInput?.value) return true;
                        return this.newInput.value === this.confirmInput.value;
                    },
                    errorMessage: "Passwords must match",
                },
            ]);
        }

        if (this.confirmInput) {
            this._safeRemoveField(this.confirmInput);
            this.validator.addField(this._sel(this.confirmInput), [
                { rule: "required", errorMessage: "Please confirm your new password" },
                {
                    validator: (value) => value === (this.newInput?.value ?? ""),
                    errorMessage: "Passwords must match",
                },
            ]);
        }
    }

    disablePasswordValidation() {
        [this.currentInput, this.newInput, this.confirmInput].forEach((el) =>
            el?.removeAttribute("required")
        );

        this._safeRemoveField(this.currentInput);
        this._safeRemoveField(this.newInput);
        this._safeRemoveField(this.confirmInput);
    }

    _safeRemoveField(inputEl) {
        if (!inputEl) return;
        try {
            this.validator.removeField?.(this._sel(inputEl));
        } catch (_) {}
    }

    _sel(inputEl) {
        if (inputEl.name) return `input[name="${CSS.escape(inputEl.name)}"]`;
        if (inputEl.id) return `#${CSS.escape(inputEl.id)}`;
        return inputEl;
    }
}